import React, { useCallback, useMemo } from 'react';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { SheetScreen } from '@/components/ScreenComponent';
import { Box } from '@/components/lib/components/Box';
import { BetEvent } from '@/feature/bets-sbk/hooks/types';
import { CashOutOffersProvider } from '@/hooks/cash-out-offers-context';
import { useFetchOnInitialFocus } from '@/hooks/use-fetch-on-initial-focus';
import { useFetchOnResume } from '@/hooks/use-fetch-resume';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { RootStackParamList } from '@/navigation/types';

import { SbkBetDetail } from '../components/SbkBetDetail';
import { useBetDetailQuery } from '../hooks/use-bet-detail';
import { useCashoutAmountQuery } from '../hooks/use-cashout';
import { useSubscribeEventsMatchUpdate } from '../hooks/use-subscribe-events-match-update';

type ScreenProps = NativeStackScreenProps<RootStackParamList, 'SbkBetScreen'>;

export const BetScreen = ({ route }: ScreenProps) => {
    const { betId, bet } = route.params;

    const { data, isLoading: isInitialLoading, refetch } = useBetDetailQuery(betId, bet);

    const canCashOut = data?.can_be_cashed_out ?? false;
    const { data: cashOutOffers, refetch: refetchOffers } = useCashoutAmountQuery(betId, canCashOut);
    // refetch bypasses enabled, so guard it — a settled bet should not hit the offer endpoint.
    const refreshOffers = useCallback(async () => {
        if (canCashOut) {
            await refetchOffers();
        }
    }, [canCashOut, refetchOffers]);

    useFetchOnInitialFocus(refetch);
    useFetchOnInitialFocus(refreshOffers);
    useFetchOnResume(refreshOffers);
    const refetchAll = useCallback(() => Promise.all([refetch(), refreshOffers()]), [refetch, refreshOffers]);
    const { forceRefreshing, onRefresh } = useForceRefresh(refetchAll);

    const eventIds = useMemo(
        () => (data?.events ? [...new Set(data.events.map((event: BetEvent) => event.id))] : []),
        [data]
    );

    useSubscribeEventsMatchUpdate(eventIds);

    if (data && !isInitialLoading) {
        return (
            <SheetScreen edges={['bottom']}>
                <CashOutOffersProvider offers={cashOutOffers}>
                    <SbkBetDetail bet={data} isRefreshing={forceRefreshing} onRefresh={onRefresh} />
                </CashOutOffersProvider>
            </SheetScreen>
        );
    }

    return (
        <SheetScreen>
            <Box flex={1} justifyContent="center" alignItems="center">
                <LoadingSpinner size={24} displayLogo={false} />
            </Box>
        </SheetScreen>
    );
};
